import { useState, useEffect } from 'react';
import { Table, DatePicker, Select, Button, Modal, Form, Input, Tag, message } from 'antd';
import { FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import { getData, createData, updateData, deleteData, downloadExcel } from '../api/api';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;
const { Search } = Input;

const TransactionQrs = ({ isAdmin, isMerchant }) => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [terminals, setTerminals] = useState([]);
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalServiceFee, setTotalServiceFee] = useState(0);
    const [totalRubAmount, setTotalRubAmount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [filters, setFilters] = useState({
        fromDate: dayjs().format('DD.MM.YYYY'),
        toDate: dayjs().format('DD.MM.YYYY'),
    });
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [downloadLoading, setDownloadLoading] = useState(false);

    const columns = [
        {
            title: t('transactionQrs.index'),
            key: 'index',
            width: 60,
            render: (_, __, index) => index + 1 + (pagination.current - 1) * pagination.pageSize,
        },
        { title: t('transactionQrs.terminalName'), dataIndex: 'terminalName', key: 'terminalName', width: 150 },
        {
            title: t('transactionQrs.amount'),
            dataIndex: 'amount',
            key: 'amount',
            width: 250,
            render: (amount) => <p>{(amount / 100).toLocaleString()} {t('transactionQrs.currencyUzs')}</p>,
        },
        {
            title: t('transactionQrs.rate'),
            dataIndex: 'rate',
            key: 'rate',
            width: 100,
            render: (rate, record) => record.currencyCode === 'UZS' ? '-' : <p>{rate}</p>,
        },
        {
            title: t('transactionQrs.currencyAmount'),
            dataIndex: 'currencyAmount',
            key: 'currencyAmount',
            width: 250,
            render: (currencyAmount, record) => record.currencyCode === 'UZS' ? '-' : <p>{currencyAmount / 100} {record.currencyCode}</p>,
        },
        {
            title: t('transactionQrs.serviceFeeAmount'),
            dataIndex: 'serviceFeeAmount',
            key: 'serviceFeeAmount',
            width: 250,
            render: (serviceFeeAmount) => <p>{serviceFeeAmount ? ((serviceFeeAmount / 100).toLocaleString() + ' ' + t('transactionQrs.currencyUzs')) : '-'}</p>,
        },
        {
            title: t('transactionQrs.status'),
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 150,
            render: (status, record) => (
                <Tag
                    color={
                        status === 50
                            ? 'green'
                            : status === 20
                                ? 'red'
                                : status === 0
                                    ? 'blue'
                                    : status === 5
                                        ? 'orange'
                                        : status === 10
                                            ? 'blue'
                                            : 'gold'
                    }
                >
                    {record.statusName}
                </Tag>
            ),
        },
        { title: t('transactionQrs.createdAt'), dataIndex: 'createdAt', key: 'createdAt', width: 180 },
        { title: t('transactionQrs.updatedAt'), dataIndex: 'updatedAt', key: 'updatedAt', width: 180 },
        {
            title: t('transactionQrs.distributionStatus'),
            dataIndex: 'distributionStatus',
            key: 'distributionStatus',
            width: 200,
            render: (distributionStatus) => (
                <Tag color={distributionStatus === 50 ? 'green' : 'red'}>
                    {distributionStatus === 50 ? t('transactionQrs.distributed') : t('transactionQrs.notDistributed')}
                </Tag>
            ),
        },
        { title: t('transactionQrs.rrn'), dataIndex: 'rrn', key: 'rrn', width: 150 },
        { title: t('transactionQrs.terminalType'), dataIndex: 'terminalType', key: 'terminalType', width: 150 },
        { title: t('transactionQrs.merchantName'), dataIndex: 'merchantName', key: 'merchantName', width: 150 },
        { title: t('transactionQrs.bankAccountName'), dataIndex: 'bankAccountName', key: 'bankAccountName', width: 150 },
        { title: t('transactionQrs.createdUser'), dataIndex: 'createdUser', key: 'createdUser', width: 350 },
        { title: t('transactionQrs.id'), dataIndex: 'id', key: 'id', width: 150 },
        { title: t('transactionQrs.link'), dataIndex: 'link', key: 'link', width: 200 },
        { title: t('transactionQrs.redirectUrl'), dataIndex: 'redirectUrl', key: 'redirectUrl', width: 200 },
    ];

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(
                isAdmin
                    ? '/admin/dashboard/transaction-qr-list-by-filter'
                    : isMerchant
                        ? '/merchant/dashboard/transaction-qr-list-by-filter'
                        : '/merchant-user/dashboard/transaction-qr-list-by-filter',
                {
                    page: pagination.current - 1,
                    size: pagination.pageSize,
                    status: -1,
                    ...filters,
                }
            );
            setData(response.data?.data?.content || []);
            setTotalAmount(response.data?.data?.details?.totalAmount || 0);
            setTotalServiceFee(response.data?.data?.details?.totalServiceFeeAmount || 0);
            setTotalRubAmount(response.data?.data?.details?.totalRubAmount || 0);
            setPagination({
                ...pagination,
                total: response.data?.data?.totalCount,
            });
        } catch (error) {
            console.error(error);
            message.error(t('transactionQrs.dataLoadError'));
        }
        setLoading(false);
    };

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            await downloadExcel(
                isAdmin
                    ? '/admin/dashboard/excel-download/transaction-qr-by-filter'
                    : isMerchant
                        ? '/merchant/dashboard/excel-download/transaction-qr-by-filter'
                        : '/merchant-user/dashboard/excel-download/transaction-qr-by-filter',
                {
                    status: -1,
                    ...filters,
                },
                'dinamikQrs.xlsx'
            );
            message.success(t('transactionQrs.successDownload'));
        } catch (error) {
            message.error(error.message || t('transactionQrs.errorDownload'));
        } finally {
            setDownloadLoading(false);
        }
    };

    const getMerchants = async () => {
        try {
            const response = await getData('/admin/dashboard/merchants-list-by-filter', {
                page: 0,
                size: 1000000,
                status: -1,
            });
            setMerchants(response.data?.data?.content || []);
        } catch (error) {
            console.error(error);
            message.error(t('transactionQrs.merchantsLoadError'));
        }
    };

    const getBankAccounts = async () => {
        const pId = filters.merchantId;
        if (!isAdmin || pId) {
            try {
                const response = await getData(
                    isAdmin
                        ? '/admin/dashboard/bank-account-list-by-filter'
                        : '/merchant/dashboard/bank-account-list-by-filter',
                    {
                        page: 0,
                        size: 10000000,
                        status: -1,
                        merchantId: pId,
                    }
                );
                setBankAccounts(response.data?.data?.content || []);
            } catch (error) {
                console.error(error);
                message.error(t('transactionQrs.bankAccountsLoadError'));
            }
        } else {
            setBankAccounts([]);
        }
    };

    const getTerminals = async () => {
        const merchantId = filters.merchantId;
        const bankAccountId = filters.bankAccountId;
        if ((bankAccountId && (!isAdmin || merchantId)) || !(isAdmin || isMerchant)) {
            try {
                const response = await getData(
                    isAdmin
                        ? '/admin/dashboard/terminal-list-by-filter'
                        : isMerchant
                            ? '/merchant/dashboard/terminal-list-by-filter'
                            : '/merchant-user/dashboard/terminal-list-by-filter',
                    {
                        page: 0,
                        size: 10000000,
                        status: -1,
                        merchantId: merchantId,
                        bankAccountId: bankAccountId,
                    }
                );
                setTerminals(response.data?.data?.content || []);
            } catch (error) {
                console.error(error);
                message.error(t('transactionQrs.terminalsLoadError'));
            }
        } else {
            setTerminals([]);
        }
    };

    const getRegions = async () => {
        try {
            const response = await getData('/admin/dashboard/region/list', null);
            console.log(response)
            setRegions(response.data?.data || []);
        } catch (error) {
            console.error(error);
            // message.error(t('terminals.merchantsLoadError'));
        }
    };

    const getDistricts = async () => {
        const rId =  filters.regionId;
        if (rId){
            try {
                const response = await getData('/admin/dashboard/district/list/by-region-id', {
                    regionId:rId
                });
                setDistricts(response.data?.data || []);
            } catch (error) {
                console.error(error);
                // message.error(t('terminals.merchantsLoadError'));
            }
        }
    };

    useEffect(() => {
        fetchData();
        if (isAdmin && (!merchants || merchants.length === 0)) {
            getMerchants();
        }
        if (isAdmin && (!regions || regions.length === 0)) {
            getRegions();
        }
    }, [pagination.current, pagination.pageSize, filters]);

    useEffect(() => {
        if (isAdmin || isMerchant) {
            getBankAccounts();
        }
        getDistricts()
    }, [filters.merchantId,filters.regionId]);

    useEffect(() => {
        getTerminals();
    }, [filters.bankAccountId, filters.merchantId]);

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const handleFilter = (values) => {
        setFilters(values);
        setPagination({ ...pagination, current: 1 });
    };

    const handleCreate = async (values) => {
        try {
            const response = await createData('/merchant-user/transaction-qr/create', {
                terminalId: values.terminalId,
                terminalType: 'E-POS',
                redirectUrl: 'https://qrpay.uz',
                amount: Number(values.amount) * 100,
                currencyCode: values.currencyCode,
            });
            if (response.data?.success) {
                message.success(t('transactionQrs.successAdd'));
                fetchData();
                setIsModalVisible(false);
                form.resetFields();
            } else {
                alert(response?.data?.error?.text || t('transactionQrs.errorAdd'));
                message.error(t('transactionQrs.errorAdd'));
            }
        } catch (error) {
            console.error(error);
            message.error(error.message || t('transactionQrs.errorAdd'));
        }
    };

    const handleUpdate = async (record) => {
        // await updateData('/dashboard', record.id, record);
        // fetchData();
    };

    const handleDelete = async (id) => {
        // await deleteData('/dashboard', id);
        // fetchData();
    };

    const showModal = () => {
        getTerminals();
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>{t('transactionQrs.title')}</h2>
                <div>
                    {!(isAdmin || isMerchant) && (
                        <Button type="primary" onClick={showModal} style={{ marginRight: 8 }}>
                            {t('transactionQrs.add')}
                        </Button>
                    )}
                    <Button
                        type="primary"
                        icon={<FileExcelOutlined />}
                        onClick={handleDownload}
                        loading={downloadLoading}
                    >
                        {t('transactionQrs.downloadExcel')}
                    </Button>
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <RangePicker
                    allowSingleDateInRange
                    value={[
                        filters.fromDate ? dayjs(filters.fromDate, 'DD.MM.YYYY') : null,
                        filters.toDate ? dayjs(filters.toDate, 'DD.MM.YYYY') : null,
                    ]}
                    format="DD.MM.YYYY"
                    size="middle"
                    onChange={(e) => {
                        if (e) {
                            const [start, end] = e;
                            handleFilter({
                                ...filters,
                                fromDate: start?.format('DD.MM.YYYY'),
                                toDate: (end ?? start)?.format('DD.MM.YYYY'),
                            });
                        }
                    }}
                />
                {isAdmin && (
                    <Select
                        style={{ width: 200 }}
                        defaultValue={null}
                        onChange={(value) => handleFilter({ ...filters, merchantId: value })}
                        size="middle"
                        placeholder={t('transactionQrs.selectMerchant')}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        notFoundContent={t('transactionQrs.notFound')}
                        dropdownStyle={{ zIndex: 9999 }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                    >
                        <Select.Option value={null}>{t('transactionQrs.allMerchants')}</Select.Option>
                        {merchants?.map((merchant) => (
                            <Select.Option key={merchant.id} value={merchant.id}>
                                {merchant.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                {(isAdmin || isMerchant) && (
                    <Select
                        style={{ width: 200 }}
                        defaultValue={null}
                        onChange={(value) => handleFilter({ ...filters, bankAccountId: value })}
                        size="middle"
                        placeholder={t('transactionQrs.selectBankAccount')}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        notFoundContent={t('transactionQrs.notFound')}
                        dropdownStyle={{ zIndex: 9999 }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        disabled={isAdmin ? !filters.merchantId : false}
                    >
                        <Select.Option value={null}>{t('transactionQrs.allBankAccounts')}</Select.Option>
                        {bankAccounts?.map((value) => (
                            <Select.Option key={value.id} value={value.id}>
                                {value.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                {(isAdmin || isMerchant) && (
                    <Select
                        style={{ width: 200 }}
                        defaultValue={null}
                        onChange={(value) => handleFilter({ ...filters, terminalId: value })}
                        size="middle"
                        placeholder={t('transactionQrs.selectTerminal')}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                        notFoundContent={t('transactionQrs.notFound')}
                        dropdownStyle={{ zIndex: 9999 }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        disabled={!(filters.bankAccountId && (!isAdmin || filters.merchantId))}
                    >
                        <Select.Option value={null}>{t('transactionQrs.allTerminals')}</Select.Option>
                        {terminals?.map((value) => (
                            <Select.Option key={value.id} value={value.id}>
                                {value.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                {
                    isAdmin ?
                        <Select
                            style={{ width: 200 }}
                            defaultValue={null}
                            onChange={(value) => handleFilter({ ...filters, regionId: value })}
                            size="middle"
                            placeholder={"Regions"}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                            notFoundContent={"Not found"}
                            dropdownStyle={{ zIndex: 9999 }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                        >
                            {regions?.map((value) => (
                                <Select.Option key={value.id} value={value.id}>
                                    {value.nameRu}
                                </Select.Option>
                            ))}
                        </Select>:' '

                }

                {
                    isAdmin ?
                        <Select
                            style={{ width: 200 }}
                            defaultValue={null}
                            onChange={(value) => handleFilter({ ...filters, districtId: value })}
                            size="middle"
                            placeholder={"Districts"}
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                            notFoundContent={"Not found"}
                            dropdownStyle={{ zIndex: 9999 }}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            disabled={!filters.regionId}
                        >
                            {districts?.map((value) => (
                                <Select.Option key={value.id} value={value.id}>
                                    {value.nameRu}
                                </Select.Option>
                            ))}
                        </Select>:' '

                }

                <Select
                    style={{ width: 200 }}
                    defaultValue={null}
                    onChange={(value) => handleFilter({ ...filters, status: value })}
                    size="middle"
                    placeholder={t('transactionQrs.selectStatus')}
                >
                    <Select.Option value={-1}>{t('transactionQrs.allStatuses')}</Select.Option>
                    <Select.Option value={50}>{t('transactionQrs.successful')}</Select.Option>
                    <Select.Option value={20}>{t('transactionQrs.canceled')}</Select.Option>
                    <Select.Option value={10}>{t('transactionQrs.inProgress')}</Select.Option>
                    <Select.Option value={5}>{t('transactionQrs.expired')}</Select.Option>
                    <Select.Option value={0}>{t('transactionQrs.created')}</Select.Option>
                </Select>
                <Search
                    placeholder={t('transactionQrs.searchPlaceholder')}
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="middle"
                    style={{ width: 300 }}
                    onSearch={(value) => handleFilter({ ...filters, search: value })}
                />
            </div>
            <Table
                columns={columns}
                dataSource={data}
                pagination={{
                    ...pagination,
                    showTotal: (total) => t('transactionQrs.total', { total }),
                }}
                loading={loading}
                onChange={handleTableChange}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                summary={() => (
                    <Table.Summary>
                        <Table.Summary.Row style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                            <Table.Summary.Cell index={0}>
                                <strong>{t('transactionQrs.summaryTotal')}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1}>
                                <strong>-</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>
                                <strong>{(totalAmount / 100).toLocaleString()} {t('transactionQrs.currencyUzs')}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3}>
                                <strong>-</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4}>
                                <strong>{(totalRubAmount / 100).toLocaleString()} {t('transactionQrs.currencyRub')}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5}>
                                <strong>{(totalServiceFee / 100).toLocaleString()} {t('transactionQrs.currencyUzs')}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} colSpan={7}></Table.Summary.Cell>
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
            <Modal
                title={t('transactionQrs.addTitle')}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="amount"
                        label={t('transactionQrs.amount')}
                        rules={[
                            { required: true, message: t('transactionQrs.amountRequired') },
                            { pattern: /^\d+$/, message: t('transactionQrs.amountPattern') },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="terminalId"
                        label={t('transactionQrs.terminal')}
                        rules={[{ required: true, message: t('transactionQrs.terminalRequired') }]}
                    >
                        <Select placeholder={t('transactionQrs.selectTerminal')}>
                            {terminals?.map((value) => (
                                <Select.Option key={value.id} value={value.id}>
                                    {value.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="currencyCode"
                        label={t('transactionQrs.qrType')}
                        rules={[{ required: true, message: t('transactionQrs.qrTypeRequired') }]}
                        initialValue={'UZS'}
                    >
                        <Select placeholder={t('transactionQrs.qrType')}>
                            <Select.Option value="UZS">{t('transactionQrs.local')}</Select.Option>
                            <Select.Option value="RUB">{t('transactionQrs.international')}</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('transactionQrs.add')}
                        </Button>
                        <Button style={{ marginLeft: 8 }} onClick={handleCancel}>
                            {t('transactionQrs.cancel')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default TransactionQrs;